/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// parts.js — discrete parts + power bricks: pure data and pure functions.
// Each def carries the part's ELECTRICAL CONTRACT for later stages:
//   - `footprint.offsets` — pin column offsets along ONE grid row (any row
//     a–j; the anchor hole is pin 1's seat).
//   - `internalBridges(params, state)` — which pin pairs are electrically
//     joined right now (Feature 70's netlist consumes this).
//   - `source(params)` — a PSU's terminal potentials (Feature 90 consumes).
//   - `normalizeParams(raw)` — coerce arbitrary stored params to valid ones.
//   - `contactBounce` — a mechanical switch whose closing contacts bounce
//     (sim/bounce.js), when Settings ▸ Spice Lite asks for it.
//   - `clickToggle(params, index)` — the params patch ONE plain click makes,
//     or null when that click changes nothing. Declaring it is ALSO what makes
//     a part click-toggling at all: the controller asks whether the function is
//     there rather than consulting a list of refs, so a part that flips under
//     the finger arrives entirely from here. `index` is the sub-position a
//     multi-switch part was clicked on (a bank's actuator), null otherwise.
//   - `momentary` — a button that conducts only while the pointer holds its
//     cap (`state.pressed`, a `chiphippo:part-state`, never a param): the
//     view binds the press gesture by this flag, not by the part's id.
//   - `joinedPins` — pin pairs joined inside the part whatever its state (a
//     tactile button's legs), listed in `internalBridges` too; the exports,
//     which read the wiring alone, join them from here.
//   - `aiBuildable: false` — off the AI builder's card (the desk review still
//     sees it): a part the compiler and verify ladder cannot yet operate.
// No electrical logic lives in views, and none in the netlist yet.

import { hd44780Unit } from "../sim/hd44780.js";
import { MM_PER_UNIT } from "../desk/desk-geometry.js";
import { ROTATIONS } from "../model/breadboard.js";
import { formatOhms } from "../model/ohm-format.js";
import { formatHz } from "../model/hertz-format.js";
import {
  RESISTOR_VALUES,
  VALUE_RANGES,
  e12Series,
} from "../model/component-value.js";
import { storedValue, valueField } from "./value-fields.js";
import { normalizeLeadOffset } from "./lead-offset.js";
import { DISCRETE_DEFS } from "./discretes.js";
import { LOAD_DEF, REGULATOR_DEFS, RELAY_DEF } from "./bench-parts.js";
import { SPEAKER_DEF } from "./speaker.js";
import { ADAPTER_DEF, AC_SUPPLY_DEF, TRANSFORMER_DEF } from "./ac-parts.js";
import { METER_DEF } from "./meter.js";
import { keepRunLatches } from "./run-latches.js";

// Re-exported: callers have always imported it from here.
export { normalizeLeadOffset };

/** The shared color choices for every colored discrete (LED, and the
    segment/bar displays) — each part's own Properties dialog + the "Default
    LED color" setting all pick from this one list. */
export const LED_COLOR_OPTIONS = Object.freeze([
  "red",
  "green",
  "blue",
  "yellow",
  "white",
]);
/** Bench supply voltages. 5 V is TTL's only rail; 3, 9, 12 and 15 V are
    CMOS rails (the CD4000B series runs 3–18 V, rated at 5/10/15 V) — and a
    trap for a 74LS part, which a supply above 5 V damages (catalog/families.js
    holds each family's range; the engine gates every chip against it). */
export const PSU_VOLTS = Object.freeze([3, 5, 9, 12, 15]);

/** A bench supply's current limits, amps (Spice Lite, features/
    spice-lite.md §4.6): past its limit a supply's voltage droops. 1 A is the
    default, and a PSU at the default stores nothing — the digital engine never
    reads it. */
export const PSU_CURRENT_LIMITS = Object.freeze([0.1, 0.25, 0.5, 1, 2, 3, 5]);
export const DEFAULT_CURRENT_LIMIT = 1;

/** A current as a supply's label says it: "500 mA", "2 A". */
export const ampsLabel = (amps) =>
  amps < 1 ? `${Math.round(amps * 1000)} mA` : `${amps} A`;
/** Clock rates (Hz) plus click-to-toggle "manual"; the timing lives in the
    renderer's SimController — the def carries only the pure contract. A 1-2-5
    ladder up two decades, then 250 Hz, 500 Hz and 1 kHz: the slow end is for
    watching an edge land, the fast end for letting a counter or a CPU
    actually get somewhere — and any other rate in CLOCK_CUSTOM_RANGE typed
    as the Rate's Custom… (2026-10-13). SimController runs edges in BATCHES between frames
    (features/done/batched-ticks.md), so no timer caps the rate — a desk too
    busy to keep up runs slower and its speed button SAYS so. 1 kHz was dropped
    once (2026-10-09, few desks kept up with it) and restored with 500 Hz
    (2026-10-10). */
export const CLOCK_HZ = Object.freeze([
  1,
  2,
  5,
  10,
  20,
  50,
  100,
  250,
  500,
  1000,
  "manual",
]);

/** A clock's Custom… rate, Hz, inclusive: any figure from 1 Hz to 20 kHz
    (component-value.js's `VALUE_RANGES.clock`, the range its box reads). */
export const CLOCK_CUSTOM_RANGE = VALUE_RANGES.clock;
/** Past this a clock's rate gets the (i) saying it may run slower: an edge
    every 250 µs is more than most desks can tick in real time, and a batch
    that cannot keep up slows the run (features/done/batched-ticks.md). */
export const CLOCK_FAST_HZ = 2000;
/** The Rate preset that opens the Custom rate box. */
export const CLOCK_CUSTOM = "custom";

/** A clock's stored rate: "manual", or a number in CLOCK_CUSTOM_RANGE —
    held to it (a desk edited by hand), anything else 1 Hz. */
function clockHz(hz) {
  if (hz === "manual") return hz;
  if (typeof hz !== "number" || !Number.isFinite(hz)) return 1;
  return Math.min(CLOCK_CUSTOM_RANGE.max, Math.max(CLOCK_CUSTOM_RANGE.min, hz)); // prettier-ignore
}

/**
 * The clock's Rate field: a PRESET list (components/preset-field.js,
 * part-properties-dialog.js's `"preset"` type) — CLOCK_HZ, Manual, then
 * Custom…, which opens a box the rate is typed into ("2500", "2.5k",
 * "2.5 kHz"), read by the one value parser and held to CLOCK_CUSTOM_RANGE.
 * `hz` stays what it always was — a number or "manual" — so nothing that
 * reads a rate knows a custom one from a preset. A rate past CLOCK_FAST_HZ
 * carries the (i) that says it may be slowed (`advise`).
 */
const CLOCK_RATE_FIELD = Object.freeze({
  key: "hz",
  label: "Rate",
  type: "preset",
  options: Object.freeze(
    CLOCK_HZ.map((hz) =>
      Object.freeze({
        value: hz,
        label: hz === "manual" ? "Manual" : formatHz(hz),
      }),
    ),
  ),
  customValue: CLOCK_CUSTOM,
  customLabel: "Custom…",
  figures: Object.freeze([
    Object.freeze({
      key: "customHz",
      label: "Custom rate",
      unit: "hertz",
      range: CLOCK_CUSTOM_RANGE,
      advise: (hz) => (hz > CLOCK_FAST_HZ ? { code: "fastClock" } : null),
    }),
  ]),
  /** The preset the card shows: the rate's own, else Custom…. */
  selected(values) {
    const hz = clockHz(values?.hz);
    return CLOCK_HZ.includes(hz) ? hz : CLOCK_CUSTOM;
  },
  /** The rate the Custom box shows: the clock's own (1 Hz for a manual
      clock, the box's floor). */
  figuresOf(values) {
    const hz = clockHz(values?.hz);
    return { customHz: hz === "manual" ? CLOCK_CUSTOM_RANGE.min : hz };
  },
  /** What picking an entry sets — the list hands back a string — or, for
      Custom…, the rate the box opens on. */
  pick(values, value) {
    if (value === CLOCK_CUSTOM) return { hz: this.figuresOf(values).customHz };
    return { hz: CLOCK_HZ.find((hz) => String(hz) === String(value)) ?? 1 };
  },
  /** A typed rate. */
  setFigure(_values, _key, hz) {
    return { hz };
  },
});

/** A stored rate as one `rates` offers: itself, the fastest offered for one
    faster than all of them (a desk saved at a rate since dropped — 1 kHz —
    keeps running as fast as it can), else `fallback`. */
function offeredHz(rates, hz, fallback) {
  if (rates.includes(hz)) return hz;
  const top = Math.max(...rates.filter((r) => typeof r === "number"));
  return Number.isFinite(hz) && hz > top ? top : fallback;
}

/** The waves a clock brick can put out. The square and the PWM — a square
    HIGH for its `duty` of each period — are LEVELS (`LEVEL_WAVES`), and run in
    both engines. The rest are Spice Lite's alone: the digital engine has no
    voltages, so there such a clock is the square its levels are, whatever it
    is set to. Each of those runs from 0 V to the clock's supply over one
    period of its rate, starting at its LOW point (spice/waves.js): a rising
    sawtooth is a "ramp up" (it climbs and drops back), a falling one a "ramp
    down" (it drops and climbs back), and a trapezoid the square with sloped
    edges. Stored only off "square", so a desk that never chose one keeps its
    bytes. */
export const CLOCK_WAVES = Object.freeze([
  "square",
  "pwm",
  "triangle",
  "trapezoid",
  "ramp-up",
  "ramp-down",
  "sine",
]);

/** The waves a clock puts out as its logic LEVEL — in both engines, edges
    the transport makes (sim/schedule.js). Every other wave is a voltage that
    moves between the edges, Spice Lite's alone. */
export const LEVEL_WAVES = Object.freeze(["square", "pwm"]);

/** Each wave's English name (the dialog translates `properties.option.<value>`). */
export const CLOCK_WAVE_LABELS = Object.freeze({
  square: "Square",
  pwm: "PWM",
  triangle: "Triangle",
  trapezoid: "Trapezoid",
  "ramp-up": "Sawtooth (ramp up)",
  "ramp-down": "Sawtooth (ramp down)",
  sine: "Sine",
});

/** A PWM clock's pulse width: the percent of each period it is HIGH. Whole
    percents, never 0 or 100 (a level that never moves is no clock), 50 — the
    square — unless said, and stored only off 50. */
export const CLOCK_DUTY_MIN = 1;
export const CLOCK_DUTY_MAX = 99;
export const CLOCK_DUTY_DEFAULT = 50;

/** A stored duty as one the clock can run: a whole percent in range, or the
    default for anything that does not read. */
function clockDuty(raw) {
  const duty = Number(raw);
  if (!Number.isFinite(duty)) return CLOCK_DUTY_DEFAULT;
  return Math.min(CLOCK_DUTY_MAX, Math.max(CLOCK_DUTY_MIN, Math.round(duty)));
}

/** What a wave's Amplitude (peak to peak) and Offset (its centre) may be
    set to, volts: anything a bench supply here reaches. Unset, a wave swings
    its whole supply about half of it (spice/waves.js `waveSwing`). */
export const CLOCK_SWING_RANGE = Object.freeze({ min: 0.01, max: 30 });

/** A stored Amplitude or Offset: a positive number of volts, or nothing. */
const clockSwingValue = (raw) =>
  typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : null;

/** The wave a clock's params name: its own, while it runs free — a manual
    clock is a switch, and a switch is square. */
function clockWaveOf(params) {
  if (params?.hz === "manual" || !CLOCK_WAVES.includes(params?.wave)) {
    return "square";
  }
  return params.wave;
}

/** A rate as a clock or can labels it: "250 Hz", "1 kHz" — the one rate
    formatter a timed part's readout uses too (model/hertz-format.js). */
export const hzLabel = (hz) => formatHz(hz);
/** An oscillator can is always free-running — a real crystal has no
    click-to-toggle pin — so it picks from CLOCK_HZ minus "manual". */
export const OSCILLATOR_HZ = Object.freeze(
  CLOCK_HZ.filter((hz) => hz !== "manual"),
);

/**
 * The HD44780 module's 16-pin interface, in datasheet order — the ONE table
 * BOTH module sizes read, because the pin assignment is identical across them
 * (it is the controller's, not the panel's). VDD/VSS are real power (the sim
 * power-gates the module like a chip); V0 (contrast) and A/K (backlight) are
 * `nc` to the logic — under Spice Lite the backlight is an LED and V0 sets the
 * glass's contrast (`LCD_BACKLIGHT`, `contrastPin`); RS/RW/E are control
 * inputs; DB0–DB7 are the bidirectional bus.
 *
 * `detail` is the datasheet's own prose for the pin, shown by the pin-
 * assignments window. Untranslated by the standing rule: per-pin datasheet
 * descriptions sit on the reference side of the line CLAUDE.md's "Language
 * support" draws, beside a part's `blurb`.
 *
 * NOTE both real modules run 16 signals through a 2.54 mm header, but neither
 * numbers it the way this app draws it: the 16×2's silkscreen counts
 * 14…1, 15, 16 left to right, and the 20×4's header is 18-way (17/18 are NC).
 * Pin 1 is at the LEFT end here, as it is for every other part in the app.
 */
const LCD_PINOUT = [
  { n: 1, name: "VSS", role: "gnd", detail: "0 V ground" },
  { n: 2, name: "VDD", role: "vcc", detail: "+5 V supply" },
  {
    n: 3,
    name: "V0",
    role: "nc",
    detail: "contrast — VDD − V0 drives the glass (Spice Lite)",
  },
  {
    n: 4,
    name: "RS",
    role: "input",
    detail: "register select — 0 cmd / 1 data",
  },
  { n: 5, name: "RW", role: "input", detail: "0 = write, 1 = read" },
  {
    n: 6,
    name: "E",
    role: "input",
    detail: "enable strobe — latches on falling edge",
  },
  { n: 7, name: "DB0", role: "io", detail: "data bus bit 0 (LSB)" },
  { n: 8, name: "DB1", role: "io", detail: "data bus bit 1" },
  { n: 9, name: "DB2", role: "io", detail: "data bus bit 2" },
  { n: 10, name: "DB3", role: "io", detail: "data bus bit 3" },
  {
    n: 11,
    name: "DB4",
    role: "io",
    detail: "data bus bit 4 (low nibble in 4-bit mode)",
  },
  { n: 12, name: "DB5", role: "io", detail: "data bus bit 5" },
  { n: 13, name: "DB6", role: "io", detail: "data bus bit 6" },
  {
    n: 14,
    name: "DB7",
    role: "io",
    detail: "data bus bit 7 (MSB / busy flag)",
  },
  { n: 15, name: "A", role: "nc", detail: "backlight LED anode (Spice Lite)" },
  {
    n: 16,
    name: "K",
    role: "nc",
    detail: "backlight LED cathode (Spice Lite)",
  },
];

/**
 * The backlight, as Spice Lite sees it (spice/lamps.js): an LED of the
 * module's colour from A to K, behind the board's own series resistor — the
 * 100 Ω (R8, marked 101) the common 1602A/2004A boards carry. That figure is
 * the boards', not the controller sheet's (which says nothing of a
 * backlight): an assumption, held in one place. It lights by its current and
 * books it; its rating is the module maker's and is not modelled (it never
 * burns or warns). In the digital engine it is cosmetic: always lit.
 */
export const LCD_BACKLIGHT = Object.freeze({
  anodePin: 15,
  cathodePin: 16,
  ohms: 100,
});

/**
 * The datasheet crop BOTH module sizes show, `web/datasheets/HD44780.png` —
 * named here rather than derived from the id, which is why `datasheet` exists
 * at all. A chip's sheet IS its id, but a module has two documents (the maker's
 * and the CONTROLLER's) and the controller's is the one this app wants: the pin
 * assignment, the bus protocol and the address maps are all the HD44780's, and
 * they are identical across the two sizes. So it is ONE file named twice, not
 * two copies of one picture — the same call the download table makes for the
 * PDF (app/datasheets/sources.js).
 */
const LCD_DATASHEET = "HD44780";

/** The 16-way header, as a linear footprint: 16 holes along one grid row. */
const LCD_FOOTPRINT = Object.freeze({
  offsets: Object.freeze(Array.from({ length: 16 }, (_, i) => i)),
});

/*
 * `characterDisplay` — the ONE data hook generic code branches on to recognise
 * a character-LCD module (never a ref or an id test; the same house rule as
 * `switchBank` and `segments`). It carries the character grid AND the module's
 * mechanical drawing, because the view, the placement ghost, the footprint box
 * and the framebuffer all have to agree and there must be exactly one source
 * for them.
 *
 *   cols/rows   the character grid
 *   headerEdge  which PCB edge the 16-way header runs along, and therefore
 *               which way the body reaches off the hole row: "bottom" → it
 *               stands UP (seat it on a top row); "top" → it hangs DOWN (a
 *               bottom row). DERIVED from pin 1, never declared — see below
 *   body        the bare PCB — also the part's box in discrete-view.js
 *   window      the display module bonded to it (the metal frame)
 *   screen      the visible glass; the live character canvas sits exactly here
 *   charPitch   one character cell, centre to centre
 *
 * Every rectangle is in PITCH UNITS with the ORIGIN AT PIN 1's HOLE — the frame
 * components/discrete-view.js draws every seated part in.
 */

/** One HD44780 character cell as the panel lays it out: 5 × 8 dots, with a
    one-dot gap to the next cell. lcd-view.js's backing buffer is the same
    model, which is what keeps the drawn cells on the module's own pitch. */
const LCD_CELL = Object.freeze({ w: 5, h: 8, gap: 1 });

/** Millimetres → pitch units, to the 5 decimals a mechanical drawing is worth
    (0.03 µm — far below anything the desk can express or a ruler can read). */
const mm = (v) => Math.round((v / MM_PER_UNIT) * 1e5) / 1e5;

/**
 * Build a character-LCD module's mechanical drawing from the numbers a RULER
 * gives you — millimetres off the real part, which is the only form these
 * measurements ever arrive in:
 *
 *   pcb     the bare board: the module's outline, and the part's own box
 *   module  the display module bonded to it (the metal frame), CENTRED on it
 *   screen  the visible glass, CENTRED in the module
 *   pin1    the first header hole, in mm from the PCB's TOP-LEFT CORNER
 *
 * CONCENTRIC-AND-CENTRED IS THE WHOLE POSITIONING RULE. There is no fourth
 * offset to measure, and no way for two of the three rectangles to disagree
 * about where they sit — which is exactly what a hand-typed set of coordinates
 * could do (and did: the numbers these replaced were a different, much larger
 * industrial module's, scaled by nothing).
 *
 * Everything is then restated about PIN 1, because that is the origin the rest
 * of the app works in (discrete-view.js draws a seated part about its first
 * hole), so the PCB's own minX/minY come out negative.
 *
 * `pin1.y` is ALSO what says which edge the header runs along — nearer the top
 * of the PCB and the body hangs DOWN off the hole row, nearer the bottom and it
 * stands UP. Deriving `headerEdge` rather than declaring it beside the geometry
 * is what stops a module being drawn one way round and seated the other.
 *
 * `charPitch` is DERIVED, not measured: the active area is the character grid
 * with its own TRAILING inter-character gap trimmed, so a span of n characters
 * is `n · (cell + gap) − gap` dots wide and one cell is `(cell + gap)` of them.
 * That is the same arithmetic lcd-view.js sizes its backing buffer with, which
 * is what lands the drawn dots on the module's pitch instead of near it.
 */
function characterDisplay({ cols, rows, pcb, module: mod, screen, pin1 }) {
  const pitch = (span, n, cell) =>
    mm(
      (span / (n * (cell + LCD_CELL.gap) - LCD_CELL.gap)) *
        (cell + LCD_CELL.gap),
    );
  const modX = (pcb.w - mod.w) / 2;
  const modY = (pcb.h - mod.h) / 2;
  const screenX = modX + (mod.w - screen.w) / 2;
  const screenY = modY + (mod.h - screen.h) / 2;
  return Object.freeze({
    cols,
    rows,
    headerEdge: pin1.y < pcb.h / 2 ? "top" : "bottom",
    body: Object.freeze({
      minX: mm(-pin1.x),
      minY: mm(-pin1.y),
      width: mm(pcb.w),
      height: mm(pcb.h),
    }),
    window: Object.freeze({
      x: mm(modX - pin1.x),
      y: mm(modY - pin1.y),
      width: mm(mod.w),
      height: mm(mod.h),
    }),
    screen: Object.freeze({
      x: mm(screenX - pin1.x),
      y: mm(screenY - pin1.y),
      width: mm(screen.w),
      height: mm(screen.h),
    }),
    charPitch: Object.freeze({
      x: pitch(screen.w, cols, LCD_CELL.w),
      y: pitch(screen.h, rows, LCD_CELL.h),
    }),
  });
}

/** ONE controller drives both module sizes, so the behavior is declared once
    and referenced by both defs. DB0–DB7 are pins 7–14. */
const HD44780_LOGIC = hd44780Unit({
  rs: 4,
  rw: 5,
  e: 6,
  db: [7, 8, 9, 10, 11, 12, 13, 14],
});

/** The Properties dialog field both module sizes share. */
const LCD_PROPERTIES = [
  {
    key: "color",
    label: "Color",
    type: "color",
    options: LED_COLOR_OPTIONS,
  },
];

/**
 * Coerce a character-LCD module's params: the backlight colour, plus the same
 * run latches a chip keeps (catalog/run-latches.js: 12 V's magic smoke and
 * Spice Lite's brown smoke).
 *
 * The damage latch is NOT optional here, unlike every other coloured discrete:
 * the engine power-gates this module like a chip (sim/engine.js powerStatus
 * reads params.damaged / params.overloaded), and SimController#persistDamage
 * round-trips the latch back through here. Drop it and a smoked module revives
 * on the next tick — and is latched again, a document change every tick.
 */
function normalizeLcdParams(raw) {
  const params = {
    color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "green",
  };
  return keepRunLatches(raw, params);
}

/** Shared by both oscillator-can sizes: a simulated rate, the current
    quarter-turn orientation, plus the same run latches a chip keeps
    (catalog/run-latches.js) — a can is a powered part with an output, so
    Spice Lite can brown-smoke it. */
function normalizeOscillatorParams(raw) {
  const params = {
    hz: offeredHz(OSCILLATOR_HZ, raw?.hz, OSCILLATOR_HZ[0]),
    rot: ROTATIONS.includes(raw?.rot) ? raw.rot : 0,
  };
  return keepRunLatches(raw, params);
}

/**
 * A bank of `n` independent SPST switches in a DIP-2n body, straddling the
 * trench like a chip: switch k bridges pin k (row e) to pin 2n+1-k (row f) —
 * the pin DIRECTLY ACROSS the trench from it (model/footprints.js's
 * pinOffset: pin p ≤ n sits at dcol p-1 in row e, pin p > n at dcol 2n-p in
 * row f — every bank is a 300-mil body, so always those two rows — and the two
 * share a column exactly when q = 2n+1-p). Every
 * position's state is durable (params.states[i]) — the CONTROLLER owns the
 * write, the view only draws it (components/discrete-view.js's house rule).
 */
function dipSwitchBankDef(n) {
  const pins = 2 * n;
  return {
    id: `sw-dip${n}`,
    kind: "discrete",
    title: `DIP switch (${n}-position)`,
    blurb:
      `${n}-position DIP switch bank in a ${pins}-pin DIP (DIP-${pins}) — ` +
      `${n} independent SPST switch${n === 1 ? "" : "es"}, each bridging the ` +
      "two pins that face each other across the trench (switch k joins pin " +
      `k in row e to pin ${pins}+1-k in row f). Click a position's actuator ` +
      "to open or close it — it stays where you put it, and stays " +
      "clickable while the simulation runs. Press R with it selected to " +
      "flip it 180° in place, same as a chip.",
    group: "Switches",
    contactBounce: true,
    // Seats and derives pins with the same footprint machinery every DIP
    // chip uses (footprints.js) — not a chip, though: electrically it's n
    // independent switches.
    package: `DIP-${pins}`,
    // The data hook generic code (the view's artwork, the controller's
    // per-position click) branches on — never an id-prefix string test.
    switchBank: true,
    pins: [
      ...Array.from({ length: n }, (_, i) => ({
        n: i + 1,
        name: `${i + 1}A`,
        role: "contact",
      })),
      ...Array.from({ length: n }, (_, i) => ({
        n: n + i + 1,
        name: `${n - i}B`, // pin n+1 faces pin n, pin 2n faces pin 1
        role: "contact",
      })),
    ],
    normalizeParams(raw) {
      const raws = Array.isArray(raw?.states) ? raw.states : [];
      return {
        // Exactly n booleans: padded with false, trimmed, junk coerced.
        states: Array.from({ length: n }, (_, i) => raws[i] === true),
        // A chip-style half-lap flip, stored only when set (as bar8iso does).
        ...(raw?.rot === 180 ? { rot: 180 } : {}),
      };
    },
    // A closed position is a HARD bridge (a real switch contact), unlike the
    // resistor's weak coupling: pin i+1 ↔ pin 2n-i, the pair facing each
    // other across the trench.
    internalBridges(params) {
      const states = params?.states;
      if (!Array.isArray(states)) return [];
      const out = [];
      for (let i = 0; i < n; i++) {
        if (states[i] === true) out.push([i + 1, pins - i]);
      }
      return out;
    },
    // A bank is the one part whose click needs the INDEX: the body between two
    // actuators is a drag handle, not a switch, so a press that names no
    // position changes nothing and must say so.
    clickToggle(params, index) {
      const states = params?.states ?? [];
      if (!Number.isInteger(index) || index < 0 || index >= states.length) {
        return null;
      }
      // COPY: the document owns the stored array, and a history snapshot must
      // never alias it.
      const next = [...states];
      next[index] = !next[index];
      return { states: next };
    },
  };
}

/**
 * The Resistance field every resistor's Properties card carries: the E12
 * values from 10 Ω to 1 MΩ to pick, or any value typed the way a bench writes
 * it (`470`, `4k7`, `2M2`, `100 kilo ohms` — model/component-value.js), and
 * refused at the field when it does not read as one, keeping the value it had
 * (value-fields.js, part-properties-dialog.js's `"combo"` type).
 */
const RESISTANCE_FIELD = valueField({
  key: "ohms",
  label: "Resistance",
  unit: "ohm",
  range: VALUE_RANGES.resistor,
  values: RESISTOR_VALUES,
  // A resistor is MADE in E12 across the whole range, past the list's 1 MΩ
  // — what a typed value's nearest-value hint is measured against.
  series: e12Series(VALUE_RANGES.resistor),
});

/** The track values a potentiometer is sold in, 100 Ω to 2 MΩ — its own
    list, not the resistor's E12: a 4.7k pot is the oddity, a 5k one the stock
    part, and the 22k/47k/220k/470k tracks sit beside the 25k/50k/250k/500k
    ones. Both what its Resistance field offers and what a typed value's
    nearest-value hint is measured against. */
const POT_VALUES = Object.freeze([
  100, 200, 500, 1e3, 2e3, 5e3, 10e3, 20e3, 22e3, 25e3, 47e3, 50e3, 100e3,
  220e3, 250e3, 470e3, 500e3, 1e6, 2e6,
]);

/** A potentiometer's Resistance: the resistor's field, offering and hinted
    against the pot's own values. */
const POT_RESISTANCE_FIELD = valueField({
  key: "ohms",
  label: "Resistance",
  unit: "ohm",
  range: VALUE_RANGES.resistor,
  // Its list is its series too (valueField's `series ?? values`).
  values: POT_VALUES,
});

/** A resistance as the loader keeps it (value-fields.js `storedValue`): a
    positive number, text an older document holds read or kept, else 10k. */
const storedOhms = (raw) => storedValue(raw?.ohms, "ohm", 10000);

/**
 * A potentiometer's wiper Position, 0–100 % — a slider in its Properties card
 * (part-properties-dialog.js's `"range"` type), applied as it is dragged. Its
 * two ENDS say what the slider means rather than where it is: the resistance
 * from the wiper to pin 1 on the left and to pin 3 on the right, `1.5k ⟵⟶
 * 8.5k` — read from the card's current values, so a new Resistance moves them
 * too. A side with no track left reads "0" (formatOhms prints nothing for
 * zero, which is right on a part's body and wrong here).
 */
const POSITION_FIELD = Object.freeze({
  key: "position",
  label: "Position",
  type: "range",
  min: 0,
  max: 100,
  step: 1,
  // Floats in a card of its own beside the desk (floating-slider.js), so it
  // can be turned while the circuit underneath stays in sight.
  float: true,
  ends: (values) => {
    // A track value that would not read (an older document's, kept as its
    // text) divides into sides of no known value.
    const side = (ohms) =>
      Number.isNaN(ohms) ? "?" : ohms > 0 ? formatOhms(ohms) : "0";
    const { toPin1, toPin3 } = potentiometerSplit(values);
    return [side(toPin1), side(toPin3)];
  },
});

/** A potentiometer's params: the whole track's value (a resistor's default)
    and the wiper's position, a whole percent, centred unless said. */
function potentiometerParams(raw) {
  const position = Number(raw?.position);
  return {
    ohms: storedOhms(raw),
    position: Number.isFinite(position)
      ? Math.min(100, Math.max(0, Math.round(position)))
      : 50,
    // Turned end for end (R): stored only when set, so an unturned part
    // round-trips byte-identical.
    ...(raw?.rot === 180 ? { rot: 180 } : {}),
  };
}

/**
 * How a potentiometer's track divides at its wiper: the resistance from the
 * wiper (pin 2) to each end — `toPin1` grows with the position, `toPin3` is the
 * rest of the track. 100k at 10 % is 10k and 90k; at 0 % pin 1's side is 0, a
 * wire. Pure arithmetic; the params are coerced first, so a half-filled object
 * divides like the part it would load as.
 * @param {{ohms?: number, position?: number}} params
 * @returns {{toPin1: number, toPin3: number}} ohms
 */
export function potentiometerSplit(params) {
  const { ohms, position } = potentiometerParams(params);
  const toPin1 = (ohms * position) / 100;
  return { toPin1, toPin3: ohms - toPin1 };
}

/** Shared by both oscillator-can sizes — the Properties dialog's rate field. */
const OSCILLATOR_PROPERTIES = [
  {
    key: "hz",
    label: "Rate",
    type: "select",
    options: OSCILLATOR_HZ.map((hz) => ({ value: hz, label: hzLabel(hz) })),
  },
];

/**
 * The 7-segment display — the real single-digit 0.56-in part (an LTS-6760 /
 * LTS-6780, a Kingbright SA/SC56-11): ten legs, five along the top and five
 * along the bottom, 0.6 in (15.24 mm) apart, so it straddles the channel as a
 * 600-mil DIP-10 does — pin 1 bottom-left, counting round anticlockwise
 * (Jason's datasheet, 2026-10-14). Seven segments a–g and the decimal point,
 * each an LED to the COMMON, which comes out twice (pins 3 and 8, joined
 * inside). Common cathode: a segment lights with its pin HIGH and the common
 * to ground; common anode: the common to +V and a segment pin pulled LOW —
 * what a 74LS47's active-low outputs do.
 *
 * Its body (12.6 × 19 mm) stands over the three rows between its pin rows,
 * as a wide chip's does, so it is ALWAYS seated at that width
 * (`normalizeParams` keeps `wide`): there is no narrow seat for a part this
 * size.
 */
const SEVEN_SEGMENT_PINS = Object.freeze({
  e: 1,
  d: 2,
  c: 4,
  dp: 5,
  b: 6,
  a: 7,
  f: 9,
  g: 10,
});
const SEVEN_SEGMENT_COMMONS = Object.freeze([3, 8]);

function sevenSegmentDef({ anode }) {
  const segRole = anode ? "cathode" : "anode";
  const comRole = anode ? "anode" : "cathode";
  const byPin = new Map(
    Object.entries(SEVEN_SEGMENT_PINS).map(([seg, pin]) => [pin, seg]),
  );
  return {
    id: anode ? "seg7ca" : "seg7cc",
    kind: "discrete",
    title: `7-segment display (common ${anode ? "anode" : "cathode"})`,
    blurb:
      "Single-digit 0.56-in 7-segment LED display with a decimal point, " +
      `common ${anode ? "ANODE" : "cathode"}: ten legs, five along the top ` +
      "and five along the bottom, 0.6 in apart, so it straddles the channel " +
      "like a wide chip. Pin 1 (e) is bottom-left, counting round " +
      "anticlockwise: 1 e, 2 d, 3 COM, 4 c, 5 dp, 6 b, 7 a, 8 COM, 9 f, " +
      "10 g. The common comes out twice (3 and 8), joined inside. " +
      (anode
        ? "Tie COM to +V and pull a segment LOW to light it — the form a " +
          "74LS47 (active-low outputs) drives directly. "
        : "Tie COM to ground and drive a segment HIGH to light it — the " +
          "form a CD4511B drives. ") +
      "Each segment is an LED: give each a series resistor. Comes in red / " +
      "green / blue / yellow / white. R turns it end for end.",
    group: "LEDs",
    package: "DIP-10-600",
    // Seated wide wherever it goes (normalizeParams) — the data hook the AI
    // compiler reads to seat it in rows d/h rather than e/f (autobuild.js).
    alwaysWide: true,
    pins: Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      const seg = byPin.get(n);
      return seg
        ? { n, name: seg, role: segRole }
        : { n, name: "COM", role: comRole };
    }),
    // Each segment is an LED between its own pin and the common (pin 3; pin 8
    // is joined to it inside) — pure data, lit by the sim-overlay with the
    // single LED's rule.
    segments: Object.freeze(
      ["a", "b", "c", "d", "e", "f", "g", "dp"].map((id) =>
        Object.freeze({
          id,
          anodePin: anode ? SEVEN_SEGMENT_COMMONS[0] : SEVEN_SEGMENT_PINS[id],
          cathodePin: anode ? SEVEN_SEGMENT_PINS[id] : SEVEN_SEGMENT_COMMONS[0],
        }),
      ),
    ),
    colors: LED_COLOR_OPTIONS,
    properties: [
      {
        key: "color",
        label: "Color",
        type: "color",
        options: LED_COLOR_OPTIONS,
      },
    ],
    normalizeParams(raw) {
      return {
        color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
        // Always the wide seat (see above) — the e/i seat's marker included,
        // which no other seat reads (footprints.js `dipRows`).
        wide: true,
        // A chip-style half-lap turn, stored only when set (as bar8iso does).
        ...(raw?.rot === 180 ? { rot: 180 } : {}),
      };
    },
    // The two commons are one strip inside the package — a hard join, as a
    // DIP switch's closed contact is.
    internalBridges() {
      return [[...SEVEN_SEGMENT_COMMONS]];
    },
  };
}

export const PART_DEFS = Object.freeze(
  [
    {
      id: "sw-slide",
      kind: "discrete",
      title: "Slide switch (SPDT)",
      blurb:
        "Single-pole double-throw slide switch — the center pin is common; " +
        "click to flip which side it bridges.",
      group: "Switches",
      contactBounce: true,
      footprint: Object.freeze({ offsets: Object.freeze([0, 1, 2]) }),
      pins: [
        { n: 1, name: "1", role: "contact" },
        { n: 2, name: "C", role: "common" },
        { n: 3, name: "2", role: "contact" },
      ],
      normalizeParams(raw) {
        return { pos: raw?.pos === "2" ? "2" : "1" };
      },
      // Common (pin 2) bridges to pin 1 or pin 3 depending on the slider.
      internalBridges(params) {
        return [[2, params?.pos === "2" ? 3 : 1]];
      },
      clickToggle(params) {
        return { pos: params?.pos === "2" ? "1" : "2" };
      },
    },
    {
      id: "sw-push",
      kind: "discrete",
      title: "Push button (momentary)",
      blurb:
        "Momentary SPST tactile button — bridges its two pins only while held.",
      group: "Switches",
      contactBounce: true,
      momentary: true,
      footprint: Object.freeze({ offsets: Object.freeze([0, 2]) }),
      pins: [
        { n: 1, name: "1", role: "contact" },
        { n: 2, name: "2", role: "contact" },
      ],
      normalizeParams() {
        return {}; // nothing durable — pressed state is transient
      },
      internalBridges(params, state) {
        return state?.pressed ? [[1, 2]] : [];
      },
    },
    {
      id: "sw-tact",
      kind: "discrete",
      title: "Tactile button (6 mm)",
      blurb:
        "The 6 mm tactile push button of every breadboard tutorial, " +
        "straddling the channel on four legs two columns apart (pins 1 and 2 " +
        "in row e, 4 and 3 facing them in row f). Each pair of legs facing " +
        "each other across the channel is one contact inside the switch, so " +
        "both halves of each column are joined; holding the button joins the " +
        "left column to the right. The column between the legs is under the " +
        "body. Press R with it selected to flip it 180° in place.",
      group: "Switches",
      contactBounce: true,
      momentary: true,
      // Off the AI builder's card (ai/catalog-brief.js BUILDABLE_DEFS).
      aiBuildable: false,
      // Seats with the DIP machinery (footprints.js), its legs a TACT-4's:
      // pins 1…2 left→right along row e, 3…4 right→left along row f, two
      // columns apart.
      package: "TACT-4",
      pins: [
        { n: 1, name: "1", role: "contact" },
        { n: 2, name: "2", role: "contact" },
        { n: 3, name: "3", role: "contact" },
        { n: 4, name: "4", role: "contact" },
      ],
      normalizeParams(raw) {
        // Pressed is transient (as sw-push's); only the half-lap turn keeps.
        return raw?.rot === 180 ? { rot: 180 } : {};
      },
      // Pins 1/4 and 2/3 are the 6.5 mm sides of the real part — one metal
      // strip each, always joined (data, so an export that reads the wiring
      // alone can join them too). The press bridges the two strips.
      joinedPins: Object.freeze([Object.freeze([1, 4]), Object.freeze([2, 3])]),
      internalBridges(params, state) {
        return [[1, 4], [2, 3], ...(state?.pressed ? [[1, 2]] : [])];
      },
    },
    {
      id: "sw-dpdt",
      kind: "discrete",
      title: "Push switch (DPDT, latching)",
      blurb:
        "The 8 mm self-locking push switch: two poles, each a common between " +
        "two throws, in a 6-pin DIP across the channel — pole 1 is pins 1·2·3 " +
        "in row e, pole 2 pins 6·5·4 facing them in row f, the commons (COM1, " +
        "COM2) in the middle. Released, each common joins its A pin (1, 6); " +
        "click it to latch it down and each common joins its B pin (3, 4) " +
        "instead; click again to release. Stays clickable while the " +
        "simulation runs. Press R with it selected to flip it 180° in place.",
      group: "Switches",
      contactBounce: true,
      // Off the AI builder's card (ai/catalog-brief.js BUILDABLE_DEFS).
      aiBuildable: false,
      package: "DIP-6",
      pins: [
        { n: 1, name: "1A", role: "contact" },
        { n: 2, name: "COM1", role: "common" },
        { n: 3, name: "1B", role: "contact" },
        { n: 4, name: "2B", role: "contact" },
        { n: 5, name: "COM2", role: "common" },
        { n: 6, name: "2A", role: "contact" },
      ],
      normalizeParams(raw) {
        return {
          on: raw?.on === true,
          // A chip-style half-lap flip, stored only when set (as bar8iso does).
          ...(raw?.rot === 180 ? { rot: 180 } : {}),
        };
      },
      // Released (`on` false) each common sits on its A throw; latched, on B.
      internalBridges(params) {
        return params?.on
          ? [
              [2, 3],
              [5, 4],
            ]
          : [
              [2, 1],
              [5, 6],
            ];
      },
      clickToggle(params) {
        return { on: !params?.on };
      },
    },
    {
      id: "sw-toggle",
      kind: "discrete",
      title: "Push button (toggle)",
      blurb:
        "Latching SPST push button — click to turn on, click again to " +
        "turn off.",
      group: "Switches",
      contactBounce: true,
      footprint: Object.freeze({ offsets: Object.freeze([0, 2]) }),
      pins: [
        { n: 1, name: "1", role: "contact" },
        { n: 2, name: "2", role: "contact" },
      ],
      normalizeParams(raw) {
        return { on: raw?.on === true };
      },
      internalBridges(params) {
        return params?.on ? [[1, 2]] : [];
      },
      clickToggle(params) {
        return { on: !params?.on };
      },
    },
    ...[1, 2, 4, 8].map(dipSwitchBankDef),
    {
      id: "led",
      kind: "discrete",
      title: "LED",
      blurb:
        "Light-emitting diode. Needs a series resistor whenever both legs " +
        "reach strongly driven nets (a supply rail, or a chip output) — " +
        "wired straight across the rails it burns out instead of lighting, " +
        "exactly as it would on a bench — unless one leg is on a 4000-series " +
        "CMOS output at 5 V or below, which is weak enough to limit the " +
        "current itself. With Spice Lite on it carries the current its " +
        "circuit really pushes through it, by its colour's datasheet: " +
        "dimmer below 10 mA, overdriven past 30 mA, burnt once its junction " +
        "overheats. Anode at the anchor hole; press F while placing to " +
        "flip polarity, R to stand it up and pick two free ends (rail or " +
        "column).",
      group: "LEDs",
      // Legs sit in ADJACENT holes — an LED needs no gap between its pins.
      footprint: Object.freeze({ offsets: Object.freeze([0, 1]) }),
      // Rotatable to the two-free-ends form (see the resistor): either leg can
      // move to any free hole, so an LED reaches any rail at any angle.
      rotatable: true,
      // One hole apart is fine — the legs only have to be in different holes.
      minSpan: 1,
      // Any def with a `colors` list arms placement with the "Default LED
      // color" setting instead of a placement-time swatch chooser (see
      // app.js's onPickChip); color is changed afterward via Properties.
      colors: LED_COLOR_OPTIONS,
      // The Properties dialog (context menu → "Properties…") — one control
      // per entry, dispatched generically by part-properties-dialog.js. Any
      // future part just adds its own `properties` list; the dialog and the
      // context-menu wiring never change.
      properties: [
        {
          key: "color",
          label: "Color",
          type: "color",
          options: LED_COLOR_OPTIONS,
        },
      ],
      pins: [
        { n: 1, name: "A", role: "anode" },
        { n: 2, name: "K", role: "cathode" },
      ],
      normalizeParams(raw) {
        const rotated = raw?.rot === 90;
        return {
          color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
          flip: raw?.flip === true,
          // Orientation: 0 = footprint form, 90 = two free ends.
          rot: rotated ? 90 : 0,
          // Pin 2's lead bend as a {dx, dy} pitch offset from the anchor —
          // only meaningful (and kept) when rotated. Resolved to a hole on
          // whatever strip lies under it; see normalizeLeadOffset.
          end: rotated ? normalizeLeadOffset(raw?.end) : null,
        };
      },
      internalBridges() {
        return []; // a diode is a device, not a bridge — Feature 90's job
      },
      // Which physical pin is the anode/cathode after an optional flip.
      polarity(params) {
        return params?.flip
          ? { anodePin: 2, cathodePin: 1 }
          : { anodePin: 1, cathodePin: 2 };
      },
    },
    {
      id: "seg8cc",
      kind: "discrete",
      title: "8-segment digit (common cathode)",
      // RETIRED (Jason, 2026-10-14): a real digit's pins are five above and
      // five below, straddling the channel — seg7cc. This one stays in the
      // catalog so every desk that has one loads and runs as it did, but the
      // tray no longer offers it.
      retired: true,
      blurb:
        "Single-block 7-segment numeric display plus decimal point (8 lit " +
        "segments), common cathode. Drive each segment anode (a–g, dp) HIGH " +
        "to light it; pin 9 (K) is the shared cathode — tie it to ground. " +
        "Comes in red / green / blue / yellow / white.",
      group: "LEDs",
      // Nine holes along one grid row: eight segment anodes then the common
      // cathode. Each segment is an LED and obeys sim/junction.js — a series
      // resistor in the common-cathode leg limits all eight at once.
      footprint: Object.freeze({
        offsets: Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8]),
      }),
      pins: [
        { n: 1, name: "a", role: "anode" },
        { n: 2, name: "b", role: "anode" },
        { n: 3, name: "c", role: "anode" },
        { n: 4, name: "d", role: "anode" },
        { n: 5, name: "e", role: "anode" },
        { n: 6, name: "f", role: "anode" },
        { n: 7, name: "g", role: "anode" },
        { n: 8, name: "dp", role: "anode" },
        { n: 9, name: "K", role: "cathode" },
      ],
      // Each segment is an LED between its anode pin and the shared cathode
      // (pin 9). Pure data — the sim-overlay lights each with the LED rule.
      segments: Object.freeze(
        ["a", "b", "c", "d", "e", "f", "g", "dp"].map((id, i) =>
          Object.freeze({ id, anodePin: i + 1, cathodePin: 9 }),
        ),
      ),
      // Same color set + Properties-dialog field as the LED — no placement-
      // time popover; color is changed afterward via the context menu.
      colors: LED_COLOR_OPTIONS,
      properties: [
        {
          key: "color",
          label: "Color",
          type: "color",
          options: LED_COLOR_OPTIONS,
        },
      ],
      normalizeParams(raw) {
        return {
          color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
        };
      },
      internalBridges() {
        return []; // segments are diodes — devices, not bridges (Feature 90)
      },
    },
    {
      id: "seg8ca",
      kind: "discrete",
      title: "8-segment digit (common anode)",
      // RETIRED, as seg8cc is — seg7ca is the real part.
      retired: true,
      blurb:
        "Single-block 7-segment numeric display plus decimal point (8 lit " +
        "segments), common ANODE. Tie pin 9 (A) to VCC; pull each segment " +
        "cathode (a–g, dp) LOW to light it — the form a 74LS47 (active-low " +
        "outputs) drives directly. Comes in red / green / blue / yellow / " +
        "white.",
      group: "LEDs",
      // Nine holes along one grid row: eight segment cathodes then the shared
      // anode. Each segment is an LED and obeys sim/junction.js — a series
      // resistor in the common-anode leg limits all eight at once.
      footprint: Object.freeze({
        offsets: Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8]),
      }),
      pins: [
        { n: 1, name: "a", role: "cathode" },
        { n: 2, name: "b", role: "cathode" },
        { n: 3, name: "c", role: "cathode" },
        { n: 4, name: "d", role: "cathode" },
        { n: 5, name: "e", role: "cathode" },
        { n: 6, name: "f", role: "cathode" },
        { n: 7, name: "g", role: "cathode" },
        { n: 8, name: "dp", role: "cathode" },
        { n: 9, name: "A", role: "anode" },
      ],
      // Each segment is an LED from the shared anode (pin 9) to its own cathode
      // pin — the mirror of seg8cc. It lights when pin 9 is HIGH and the segment
      // pin is driven LOW (the LED rule in sim-overlay), which is exactly what a
      // 74LS47's active-low outputs do.
      segments: Object.freeze(
        ["a", "b", "c", "d", "e", "f", "g", "dp"].map((id, i) =>
          Object.freeze({ id, anodePin: 9, cathodePin: i + 1 }),
        ),
      ),
      // Same color set + Properties-dialog field as the LED — no placement-
      // time popover; color is changed afterward via the context menu.
      colors: LED_COLOR_OPTIONS,
      properties: [
        {
          key: "color",
          label: "Color",
          type: "color",
          options: LED_COLOR_OPTIONS,
        },
      ],
      normalizeParams(raw) {
        return {
          color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
        };
      },
      internalBridges() {
        return []; // segments are diodes — devices, not bridges (Feature 90)
      },
    },
    sevenSegmentDef({ anode: false }),
    sevenSegmentDef({ anode: true }),
    {
      id: "bar8",
      kind: "discrete",
      title: "8-segment LED bar",
      blurb:
        "Eight-segment LED bar graph, common cathode. Drive each bar's anode " +
        "(1–8) HIGH to light it; pin 9 (K) is the shared cathode — tie it to " +
        "ground. Comes in red / green / blue / yellow / white.",
      group: "LEDs",
      // Nine holes along one grid row: eight bar anodes then the common cathode.
      footprint: Object.freeze({
        offsets: Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8]),
      }),
      // R turns it end for end (params.rot 180), as the resistor array does:
      // the same nine holes, the numbering backwards — the cathode at the
      // left — and its block swung round to the other side of the row.
      reversible: true,
      pins: [
        { n: 1, name: "1", role: "anode" },
        { n: 2, name: "2", role: "anode" },
        { n: 3, name: "3", role: "anode" },
        { n: 4, name: "4", role: "anode" },
        { n: 5, name: "5", role: "anode" },
        { n: 6, name: "6", role: "anode" },
        { n: 7, name: "7", role: "anode" },
        { n: 8, name: "8", role: "anode" },
        { n: 9, name: "K", role: "cathode" },
      ],
      segments: Object.freeze(
        ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"].map((id, i) =>
          Object.freeze({ id, anodePin: i + 1, cathodePin: 9 }),
        ),
      ),
      // Same color set + Properties-dialog field as the LED — no placement-
      // time popover; color is changed afterward via the context menu.
      colors: LED_COLOR_OPTIONS,
      properties: [
        {
          key: "color",
          label: "Color",
          type: "color",
          options: LED_COLOR_OPTIONS,
        },
      ],
      normalizeParams(raw) {
        return {
          color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
          // The end-for-end turn, stored only when set.
          ...(raw?.rot === 180 ? { rot: 180 } : {}),
        };
      },
      internalBridges() {
        return []; // each bar is a diode — a device, not a bridge (Feature 90)
      },
    },
    {
      id: "bar8iso",
      kind: "discrete",
      title: "8-segment LED bar (isolated)",
      blurb:
        "Eight-segment LED bar graph in a 16-pin DIP package — each bar is an " +
        "INDEPENDENT LED with its own anode and cathode (no shared pin). It " +
        "straddles the trench like a chip: anodes A1–A8 in row e, cathodes " +
        "K1–K8 in row f. Drive a bar's anode HIGH and pull its cathode LOW to " +
        "light it. Comes in red / green / blue / yellow / white. Press R with it " +
        "selected to flip it 180° in place, same as a chip — the holes it " +
        "occupies never move, only which anode/cathode sits in which; its " +
        "pin dialog updates to match.",
      group: "LEDs",
      // A 16-pin DIP straddling the trench: the anode/cathode of each bar face
      // each other across a column, so it seats and derives pins with the same
      // footprint machinery every DIP chip uses (footprints.js). Not a chip,
      // though — electrically it's eight LEDs, lit by the sim-overlay.
      package: "DIP-16",
      // Anodes A1–A8 are pins 1–8 (row e, left→right); cathodes K8–K1 are pins
      // 9–16 (row f, right→left), so bar i's cathode (pin 17-i) sits directly
      // across the trench from its anode (pin i).
      pins: [
        ...Array.from({ length: 8 }, (_, i) => ({
          n: i + 1,
          name: `A${i + 1}`,
          role: "anode",
        })),
        ...Array.from({ length: 8 }, (_, i) => ({
          n: i + 9,
          name: `K${8 - i}`,
          role: "cathode",
        })),
      ],
      // Each bar is an LED between its own anode pin and its own cathode pin —
      // pure data, lit by the sim-overlay with the same rule as a single LED.
      segments: Object.freeze(
        Array.from({ length: 8 }, (_, i) =>
          Object.freeze({
            id: `s${i + 1}`,
            anodePin: i + 1,
            cathodePin: 16 - i,
          }),
        ),
      ),
      // Same color set + Properties-dialog field as the LED — no placement-
      // time popover; color is changed afterward via the context menu.
      colors: LED_COLOR_OPTIONS,
      properties: [
        {
          key: "color",
          label: "Color",
          type: "color",
          options: LED_COLOR_OPTIONS,
        },
      ],
      // `rot: 180` is a chip-style half-lap flip (model/occupancy.js's
      // `def.package` branch) — same holes, reversed pin numbering; press R
      // with it selected. Preserve it the same way CHIP_DEFS's shared
      // normalizeParams does (catalog/index.js) — this def isn't wrapped by
      // that, since it's a PART_DEFS discrete, not a chip.
      normalizeParams(raw) {
        return {
          color: LED_COLOR_OPTIONS.includes(raw?.color) ? raw.color : "red",
          ...(raw?.rot === 180 ? { rot: 180 } : {}),
        };
      },
      internalBridges() {
        return []; // each bar is a diode — a device, not a bridge (Feature 90)
      },
    },
    {
      id: "resistor",
      kind: "discrete",
      title: "Resistor",
      blurb:
        "Two-terminal resistor. In this logic-level sim it's a WEAK coupler: " +
        "it conducts one end's driven level to the other at a strength below " +
        "any chip output, so it behaves as a pull-up / pull-down / series " +
        "resistor. Its Resistance (Properties) is drawn as its colour bands " +
        "and read by the timing parts (the 555, the 4000-series timers); to " +
        "everything else it is cosmetic (no analog current here). " +
        "Press R while placing to stand it vertically and pick two free ends " +
        "(e.g. a power rail and a grid column).",
      group: "Resistors",
      footprint: Object.freeze({ offsets: Object.freeze([0, 3]) }),
      properties: [RESISTANCE_FIELD],
      // Rotatable to a vertical, two-free-ends form: pin 1 at the anchor hole,
      // pin 2 bent to the `params.end` offset. The seating model switches from
      // footprint-offset to a free lead, so pin 2 can reach ANY hole at any
      // angle — including one on a neighbouring strip, e.g. a power rail.
      rotatable: true,
      // Leads can't be bent closer than the body is long: the two ends must
      // sit at least this far apart (pitch units). 2.5 is a quarter-watt body
      // (~6.3 mm) and NOT the 3 it used to be, which was a lattice artefact:
      // the closest pins across a rail↔pin-board dovetail are 2.76 apart
      // (7.0 mm, measured — board-types.js), so a whole-pitch minimum forbade
      // the commonest bench move there is, taking power off the rail into a
      // row. In-row spans are unaffected: two columns is still 2, still too
      // close, and three is still the shortest that fits.
      minSpan: 2.5,
      pins: [
        { n: 1, name: "1", role: "lead" },
        { n: 2, name: "2", role: "lead" },
      ],
      normalizeParams(raw) {
        const rotated = raw?.rot === 90;
        return {
          ohms: storedOhms(raw),
          // Orientation: 0 = horizontal footprint, 90 = vertical two-end form.
          rot: rotated ? 90 : 0,
          // Pin 2's lead bend as a {dx, dy} pitch offset from the anchor —
          // only meaningful (and kept) when rotated. Resolved to a hole on
          // whatever strip lies under it; see normalizeLeadOffset.
          end: rotated ? normalizeLeadOffset(raw?.end) : null,
        };
      },
      // A resistor is NOT a hard conductor — its two ends stay separate nets
      // (unlike a wire or a closed switch), so it declares no internal bridges.
      internalBridges() {
        return [];
      },
      // …instead the two leads are WEAKLY coupled: the simulator (resolve.js's
      // PULL tier) conducts one end's strong H/L to the other at the weakest
      // drive strength. `weakBridges` lists the coupled pin pairs (data, not a
      // code path) — a resistor could carry more, but this one bridges 1↔2.
      weakBridges() {
        return [[1, 2]];
      },
    },
    {
      id: "rnet9",
      kind: "discrete",
      title: "Resistor array (bussed, 9-pin)",
      blurb:
        "Bussed resistor network in a 9-pin SIP, numbered as the real part " +
        "is: pin 1 (COM) is the shared bus — the end the printed dot marks — " +
        "and pins 2–9 each reach it through their own resistor. Tie COM to " +
        "ground for eight pull-downs, or to +V for eight pull-ups — like the " +
        "single resistor, each element is a WEAK coupler (below any chip " +
        "output), never a hard connection. Press R while placing, or with it " +
        "selected, to turn it end-for-end: the dot, pin 1 and the common bus " +
        "all move to the other end. Its Resistance (Properties, each " +
        "element's) is read by the timing parts and otherwise cosmetic.",
      group: "Resistors",
      properties: [RESISTANCE_FIELD],
      // Nine holes along one grid row: the common bus first (pin 1, at the
      // anchor — the marked end), then the eight resistor pins.
      footprint: Object.freeze({
        offsets: Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8]),
      }),
      // Turns end-for-end in place (params.rot 180). The holes are evenly
      // spaced, so the flipped part covers the SAME nine — only the numbering
      // reverses, exactly as a DIP's half lap does (model/occupancy.js). The
      // hook is data, so nothing branches on the id; the catalog test holds
      // every `reversible` def to a palindromic offset list, which is what
      // makes "same holes, reversed pins" true.
      reversible: true,
      pins: [
        { n: 1, name: "COM", role: "common" },
        ...Array.from({ length: 8 }, (_, i) => ({
          n: i + 2,
          name: `${i + 2}`,
          // Named for its own pin number, so `rnet9.4` has one reading —
          // model/pin-resolve.js reports an ambiguity when a part's names and
          // numbers disagree, and a bussed SIP's element pins have no other
          // name on any datasheet.
          role: "lead",
        })),
      ],
      normalizeParams(raw) {
        return {
          ohms: storedOhms(raw),
          // Stored only when set (as sw-dip8 and bar8iso do), so an unturned
          // part round-trips byte-identical.
          ...(raw?.rot === 180 ? { rot: 180 } : {}),
        };
      },
      // Like the single resistor, an element never hard-bridges — its two ends
      // stay separate nets (the coupling is weak, below any chip output).
      internalBridges() {
        return [];
      },
      // …instead each of pins 2–9 is WEAKLY coupled to the common bus (pin 1):
      // the simulator's PULL tier conducts COM's strong level out to every free
      // pin at the weakest strength. Eight independent pulls, one shared bus.
      // Stated in PIN numbers, so a flipped part needs nothing here — the pins
      // move, the elements they name do not.
      weakBridges() {
        return Array.from({ length: 8 }, (_, i) => [i + 2, 1]);
      },
    },
    {
      id: "pot",
      kind: "discrete",
      title: "Potentiometer",
      blurb:
        "Three-pin trimmer potentiometer: a resistive track between pins 1 " +
        "and 3, and a wiper (pin 2, the middle) that taps it. Set its " +
        "Resistance — the whole track — and the wiper's Position, 0–100 %, " +
        "in Properties. From the wiper to pin 1 is Position × Resistance and " +
        "to pin 3 the rest: 100k at 10 % is 10k to pin 1 and 90k to pin 3. " +
        "Like a resistor, each side is a WEAK coupler, read by the timing " +
        "parts — except a side with nothing left of the track (pin 1 at 0 %, " +
        "pin 3 at 100 %), which is a wire, and an LED fed through it burns. " +
        "Press R while placing, or with it selected, to turn it end for end: " +
        "pins 1 and 3 swap ends and the screw moves to the other side.",
      group: "Resistors",
      // A Bourns 3296W-style trimmer: three pins in a row at 0.1 in, the
      // wiper in the middle.
      footprint: Object.freeze({ offsets: Object.freeze([0, 1, 2]) }),
      // Turns end for end in place (params.rot 180), as a transistor does:
      // the same three holes, pins 1 and 3 swapped; the wiper stays put.
      reversible: true,
      properties: [POT_RESISTANCE_FIELD, POSITION_FIELD],
      pins: [
        {
          n: 1,
          name: "1",
          role: "lead",
          detail: "track end — Position × R from the wiper",
        },
        {
          n: 2,
          name: "W",
          role: "wiper",
          detail: "wiper — taps the track",
        },
        {
          n: 3,
          name: "3",
          role: "lead",
          detail: "track end — (100 % − Position) × R from the wiper",
        },
      ],
      // Whole percent, clamped: the slider's own steps, and the one number
      // the two sides' arithmetic is stated in.
      normalizeParams: potentiometerParams,
      // A side with NONE of the track left is not a resistor at all but the
      // wiper's contact sitting on the end terminal — a WIRE, so the nets
      // join and nothing limits the current through them.
      // Which side that is, is the POSITION's to say, not the arithmetic's:
      // a track value that would not read (an older document's, kept as its
      // text) still has a wiper, and its sides are still resistors.
      internalBridges(params) {
        const { position } = potentiometerParams(params);
        return [
          ...(position === 0 ? [[2, 1]] : []),
          ...(position === 100 ? [[2, 3]] : []),
        ];
      },
      // Every other side is a resistor: a weak coupler from the wiper to that
      // end. Each pair carries its OWN ohms (third element), since the two
      // sides divide one track — sim/rc-trace.js reads it in place of the
      // part's single `ohms`, which every other consumer can ignore (and
      // skips a side of no known value, as it does a resistor's).
      weakBridges(params) {
        const { position } = potentiometerParams(params);
        const { toPin1, toPin3 } = potentiometerSplit(params);
        return [
          ...(position > 0 ? [[2, 1, toPin1]] : []),
          ...(position < 100 ? [[2, 3, toPin3]] : []),
        ];
      },
    },
    // The discretes — capacitors, diodes, inductors, transistors.
    ...DISCRETE_DEFS,
    // The bench parts (bench-parts.js): the regulators and the relay.
    ...REGULATOR_DEFS,
    RELAY_DEF,
    // The speaker (speaker.js) — Spice Lite's alone.
    SPEAKER_DEF,
    {
      id: "psu",
      kind: "psu",
      title: "Power supply",
      blurb:
        "Bench power brick (3 V / 5 V / 9 V / 12 V / 15 V) with addressable " +
        "+ and − terminals — wire them into a board's rails.",
      group: "Power",
      // Desk outline (pitch units) and terminal pads at INTEGER offsets so
      // wired terminals land on the global 0.1-in lattice.
      size: Object.freeze({ width: 8, height: 5 }),
      terminals: [
        { id: "+", dx: 2, dy: 4 },
        { id: "-", dx: 6, dy: 4 },
      ],
      // The Properties dialog (context menu → "Properties…") — a live input,
      // so this field applies with no lock/undo-gap even while the sim runs.
      properties: [
        {
          key: "volts",
          label: "Voltage",
          type: "select",
          options: PSU_VOLTS.map((v) => ({ value: v, label: `${v} V` })),
        },
        {
          key: "currentLimit",
          label: "Current limit",
          type: "select",
          default: DEFAULT_CURRENT_LIMIT,
          options: PSU_CURRENT_LIMITS.map((a) => ({
            value: a,
            label: ampsLabel(a),
          })),
        },
      ],
      normalizeParams(raw) {
        const volts = PSU_VOLTS.includes(raw?.volts) ? raw.volts : 5;
        const limit = PSU_CURRENT_LIMITS.includes(raw?.currentLimit)
          ? raw.currentLimit
          : DEFAULT_CURRENT_LIMIT;
        // Omitted at the default, so every existing desk keeps its bytes.
        return limit === DEFAULT_CURRENT_LIMIT
          ? { volts }
          : { volts, currentLimit: limit };
      },
      // Terminal potentials for the simulator (Feature 90).
      source(params) {
        return { plus: params?.volts ?? 5, minus: 0 };
      },
    },
    {
      id: "clock",
      kind: "clock",
      title: "Clock source",
      blurb:
        "Square-wave clock (1 Hz up to 1 kHz, or manual click-to-toggle). " +
        "It runs from a supply like any instrument: wire " +
        "`vcc` to the + rail and `gnd` to the − rail, and `out` to a chip's " +
        "clock pin — its HIGH is that supply's voltage. Unpowered it stops. " +
        "As PWM it is HIGH for its pulse width (1–99 %) of each period. " +
        "Under Spice Lite it can put out a triangle, a trapezoid, a sawtooth " +
        "or a sine instead, swinging from 0 V to its supply — or set its " +
        "Amplitude (peak to peak) and Offset (its centre), clipped at 0 V " +
        "and its supply.",
      group: "Power",
      size: Object.freeze({ width: 8, height: 5 }),
      // `out` and `gnd` keep the places they always had; the supply terminal
      // sits between them. It is `vcc`, never `+`: an address ending in `+`
      // is read as a PSU's own terminal (schematic-layout.js `netPolarity`).
      terminals: [
        { id: "out", dx: 2, dy: 4 },
        { id: "vcc", dx: 4, dy: 4 },
        { id: "gnd", dx: 6, dy: 4 },
      ],
      // The Properties dialog — a live setting, so it applies while running.
      properties: [
        CLOCK_RATE_FIELD,
        {
          // The levels in both engines; every other wave is Spice Lite's
          // alone (an option's `spiceOnly`: the digital engine runs it square).
          key: "wave",
          label: "Wave type",
          type: "select",
          default: "square",
          options: CLOCK_WAVES.map((value) => ({
            value,
            label: CLOCK_WAVE_LABELS[value],
            ...(LEVEL_WAVES.includes(value) ? {} : { spiceOnly: true }),
          })),
        },
        {
          // A PWM's pulse width, applied as it is dragged; greyed for any
          // other wave (and a manual clock, which is square).
          key: "duty",
          label: "Pulse width",
          type: "range",
          min: CLOCK_DUTY_MIN,
          max: CLOCK_DUTY_MAX,
          step: 1,
          default: CLOCK_DUTY_DEFAULT,
          valuePercent: true,
          disabledWhen: (values) => clockWaveOf(values) !== "pwm",
        },
        // A wave's swing (Spice Lite's alone, as the waves are): blank, the
        // whole supply about half of it; greyed for a level.
        {
          ...valueField({
            key: "amplitude",
            label: "Amplitude",
            unit: "volt",
            range: CLOCK_SWING_RANGE,
            values: [0.1, 0.2, 0.5, 1, 2, 3.3, 5, 9, 12],
            optional: true,
          }),
          spiceOnly: true,
          disabledWhen: (values) => LEVEL_WAVES.includes(clockWaveOf(values)),
        },
        {
          ...valueField({
            key: "offset",
            label: "Offset",
            unit: "volt",
            range: CLOCK_SWING_RANGE,
            values: [0.5, 1, 1.65, 2.5, 4.5, 6, 7.5],
            optional: true,
          }),
          spiceOnly: true,
          disabledWhen: (values) => LEVEL_WAVES.includes(clockWaveOf(values)),
        },
      ],
      normalizeParams(raw) {
        const hz = clockHz(raw?.hz);
        const wave = CLOCK_WAVES.includes(raw?.wave) ? raw.wave : "square";
        if (wave === "square") return { hz };
        const duty = clockDuty(raw?.duty);
        if (wave === "pwm") {
          return duty !== CLOCK_DUTY_DEFAULT ? { hz, wave, duty } : { hz, wave }; // prettier-ignore
        }
        // A wave keeps its swing, each only where set.
        const amplitude = clockSwingValue(raw?.amplitude);
        const offset = clockSwingValue(raw?.offset);
        return {
          hz,
          wave,
          ...(amplitude != null ? { amplitude } : {}),
          ...(offset != null ? { offset } : {}),
        };
      },
      /** Is this clock free-running (has a rate) rather than manual? */
      isAuto(params) {
        return params?.hz !== "manual";
      },
      /** The wave it puts out: its own, while it runs free — a manual clock
          is a switch, and a switch is square. (A wave not in LEVEL_WAVES is
          put out under Spice Lite only.) */
      waveOf(params) {
        return clockWaveOf(params);
      },
      /** The share of each period it is HIGH, 0–1: a free-running PWM's
          pulse width, else the square's half. */
      dutyOf(params) {
        return clockWaveOf(params) === "pwm"
          ? clockDuty(params?.duty) / 100
          : 0.5;
      },
    },
    LOAD_DEF,
    // The AC bricks (ac-parts.js) — Spice Lite's alone.
    AC_SUPPLY_DEF,
    ADAPTER_DEF,
    TRANSFORMER_DEF,
    // The multimeter (meter.js) — Spice Lite's alone.
    METER_DEF,
    {
      id: "osc-full",
      kind: "discrete",
      title: "Oscillator (full can)",
      blurb:
        "Crystal-oscillator can, full size — a rectangular metal can with " +
        "just 4 legs at its corners (7 holes by 4 holes, body overhanging " +
        "half a hole on every side). Seats anywhere, any row — including " +
        "straddling the centre channel; press R while placing to spin the " +
        "ghost a quarter turn at a time, or with it selected to flip an " +
        "already-seated can end-for-end (180°). A free-running square-wave " +
        "source, powered like a chip: NC, GND, OUTPUT, VCC.",
      group: "Oscillators",
      // The rigid footprint's full pitch-unit extents (rot 0): 6 units long
      // (7 holes) by 3 units deep (4 holes) — see model/occupancy.js's
      // `def.can` branch and model/breadboard.js's rotateOffset for how the
      // 4 corner pins derive from this at any quarter-turn.
      can: Object.freeze({ width: 6, height: 3 }),
      pins: [
        { n: 1, name: "NC", role: "nc" },
        { n: 2, name: "GND", role: "gnd" },
        { n: 3, name: "OUT", role: "output" },
        { n: 4, name: "VCC", role: "vcc" },
      ],
      // A self-clocking source: the engine drives the output
      // pin from clockPhase instead of evaluating logic.units.
      logic: Object.freeze({ oscillator: true }),
      properties: OSCILLATOR_PROPERTIES,
      normalizeParams: normalizeOscillatorParams,
      internalBridges() {
        return []; // the output is DRIVEN by the engine, never a passive bridge
      },
    },
    {
      id: "osc-half",
      kind: "discrete",
      title: "Oscillator (half can)",
      blurb:
        "Crystal-oscillator can, half size — a rectangular metal can with " +
        "just 4 legs at its corners (4 holes by 4 holes, body overhanging " +
        "half a hole on every side). Seats anywhere, any row — including " +
        "straddling the centre channel — and turns in 90° steps; press R " +
        "while placing (or with it selected) to spin it. NC, GND, OUTPUT, VCC.",
      group: "Oscillators",
      // 3 pitch-units square (4 holes by 4 holes) at rot 0.
      can: Object.freeze({ width: 3, height: 3 }),
      pins: [
        { n: 1, name: "NC", role: "nc" },
        { n: 2, name: "GND", role: "gnd" },
        { n: 3, name: "OUT", role: "output" },
        { n: 4, name: "VCC", role: "vcc" },
      ],
      logic: Object.freeze({ oscillator: true }),
      properties: OSCILLATOR_PROPERTIES,
      normalizeParams: normalizeOscillatorParams,
      internalBridges() {
        return [];
      },
    },
    {
      id: "lcd16x2",
      kind: "discrete",
      title: "Character LCD 16×2 (HD44780)",
      blurb:
        "Hitachi HD44780 character-LCD module, 16 columns × 2 rows (the " +
        "standard 1602A, 80 × 36 mm). Its 16-way header runs along the " +
        "module's TOP edge and plugs into 16 holes along one row, so the body " +
        "hangs BELOW that row — seat it on a bottom row (a) and it clears the " +
        "board it is plugged into. Wire " +
        "VDD/VSS to a 5 V rail, then drive it over the parallel bus: put a " +
        "command or character code on DB0–DB7, set RS (0 = instruction, " +
        "1 = data) and R/W (0 = write), and pulse E — the byte latches on E's " +
        "falling edge. V0 (contrast) and A/K (backlight) are cosmetic, except " +
        "under Spice Lite: there the backlight is an LED (A to K) and VDD − V0 " +
        "drives the glass — V0 at VDD, or left open, blanks it. " +
        "During a read the module drives DB0–DB7, so tri-state whatever else " +
        "is on the bus.",
      group: "Displays",
      footprint: LCD_FOOTPRINT,
      pins: LCD_PINOUT.map((p) => Object.freeze({ ...p })),
      datasheet: LCD_DATASHEET,
      // Measured off a real 1602A: 80 × 36 mm of PCB, a 71 × 24 mm module, and
      // 65 × 15 mm of visible glass, with pin 1's centre 7.5 mm in from the left
      // edge and 2.5 mm down from the top one.
      characterDisplay: characterDisplay({
        cols: 16,
        rows: 2,
        pcb: { w: 80, h: 36 },
        module: { w: 71, h: 24 },
        screen: { w: 65, h: 15 },
        pin1: { x: 7.5, y: 2.5 },
      }),
      colors: LED_COLOR_OPTIONS,
      properties: LCD_PROPERTIES,
      normalizeParams: normalizeLcdParams,
      backlight: LCD_BACKLIGHT,
      contrastPin: 3,
      internalBridges() {
        return []; // a module is a device, not a bridge — Feature 90's job
      },
      logic: HD44780_LOGIC,
    },
    {
      id: "lcd20x4",
      kind: "discrete",
      title: "Character LCD 20×4 (HD44780)",
      blurb:
        "Hitachi HD44780 character-LCD module, 20 columns × 4 rows (the " +
        "standard 2004A, 98 × 60 mm). The pin assignment is identical to the " +
        "16×2's, and so is the seating: the header is along the module's TOP " +
        "edge, so the body hangs BELOW the row it plugs into — seat it on a " +
        "bottom row (a) and it clears the board. Wire VDD/VSS to a 5 V rail, " +
        "then drive it over " +
        "the parallel bus: put a command or character code on DB0–DB7, set RS " +
        "(0 = instruction, 1 = data) and R/W (0 = write), and pulse E — the " +
        "byte latches on E's falling edge. V0 (contrast) and A/K (backlight) " +
        "are cosmetic, except under Spice Lite: there the backlight is an LED " +
        "(A to K) and VDD − V0 drives the glass — V0 at VDD, or left open, " +
        "blanks it. During a read the module drives DB0–DB7, so " +
        "tri-state whatever else is on the bus.",
      group: "Displays",
      footprint: LCD_FOOTPRINT,
      pins: LCD_PINOUT.map((p) => Object.freeze({ ...p })),
      datasheet: LCD_DATASHEET,
      // Measured off a real 2004A: 98 × 60 mm of PCB, a 97 × 40 mm module (it
      // spans very nearly the full width — the bands the header and the badge
      // live in are the ones above and below it), and 77 × 26 mm of glass. The
      // header sits the SAME 2.5 mm off its edge as the 16×2's: the boards are
      // different sizes, the row of pins is the same part on both.
      characterDisplay: characterDisplay({
        cols: 20,
        rows: 4,
        pcb: { w: 98, h: 60 },
        module: { w: 97, h: 40 },
        screen: { w: 77, h: 26 },
        pin1: { x: 8.5, y: 2.5 },
      }),
      colors: LED_COLOR_OPTIONS,
      properties: LCD_PROPERTIES,
      normalizeParams: normalizeLcdParams,
      backlight: LCD_BACKLIGHT,
      contrastPin: 3,
      internalBridges() {
        return [];
      },
      logic: HD44780_LOGIC,
    },
  ].map(Object.freeze),
);
